import { BrandBar } from "@/components/layout/BrandBar";
import { OrgLockup } from "@/components/layout/OrgLockup";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrg, useSession } from "@/hooks";
import { visibleNav } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { authService } from "@/services";
import {
	BarChart3,
	CreditCard,
	FileText,
	LogOut,
	Network,
	Radar,
	ScrollText,
	Settings,
	Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

const NAV = [
	{ to: "/seats", label: "Seats", icon: Users, module: "seats" },
	{ to: "/people", label: "People", icon: BarChart3, module: "people" },
	{ to: "/analytics", label: "Analytics", icon: Radar, module: "analytics" },
	{ to: "/teams", label: "Teams", icon: Network, module: "teams" },
	{ to: "/content", label: "Content", icon: FileText, module: "content" },
	{ to: "/billing", label: "Billing", icon: CreditCard, module: "billing" },
	{ to: "/activity", label: "Activity log", icon: ScrollText, module: "audit" },
	{ to: "/settings", label: "Settings", icon: Settings, module: "settings" },
] as const;

/**
 * Sidebar left, content right.
 *
 * The sidebar is the app's fixed furniture — identity at the top, navigation in
 * the middle, the account and the way out pinned to the bottom. Nothing in it
 * scrolls with the page, so where you are and who you are stay legible however
 * far down a long roster you have gone.
 *
 * The content plane is centred and width-capped rather than filling the
 * viewport. Tables of people and invoices are the main thing here, and a table
 * stretched across a 27-inch monitor puts a name and its date so far apart that
 * the eye loses the row.
 */
export function AppShell({ children }: { children: ReactNode }) {
	const { data, isLoading } = useOrg();
	const { data: me } = useSession();
	const { pathname } = useLocation();

	// Which tabs this partner sees. The rule lives in lib/nav.ts so it can be
	// tested — inline, a mistake in it shows up only as a feature nobody can
	// find.
	const nav = visibleNav(
		NAV,
		{
			suspended: data?.org.status === "suspended",
			unstarted: data ? !data.org.contract_starts_at : false,
		},
		{ role: data?.org.role },
	);

	return (
		// h-screen + overflow-hidden, so the shell is fixed furniture and only the
		// content scrolls. Sizing falls out of flexbox rather than a calc against
		// the BrandBar's height — that bar is 3px AND returns null when a partner
		// has set no accent, so any hardcoded offset is wrong in the common case.
		<div className="flex h-screen flex-col overflow-hidden bg-surface2">
			{/* The partner's own colour, spanning everything — it frames the whole
			    app rather than decorating one corner of it. */}
			<BrandBar accent={data?.org.settings.accent} />

			<div className="flex min-h-0 flex-1 flex-col md:flex-row">
				<aside
					className={cn(
						"shrink-0 border-divider bg-surface-primary",
						// A column beside the content on a real screen; a strip
						// above it on a phone, where 240px of chrome would leave
						// nothing for the thing you came to read.
						"border-b md:h-full md:w-60 md:border-b-0 md:border-r",
						"md:flex md:flex-col",
					)}
				>
					<div className="flex items-center gap-2.5 px-5 py-4">
						{isLoading ? (
							<Skeleton className="h-6 w-32" />
						) : (
							<>
								{data?.org.settings.logo_url ? (
									<img
										src={data.org.settings.logo_url}
										alt=""
										className="size-7 shrink-0 rounded object-contain"
									/>
								) : null}
								<OrgLockup name={data?.org.name} className="text-body" />
							</>
						)}
					</div>

					<nav className="px-3 pb-3 md:flex-1">
						<ul className="flex gap-0.5 overflow-x-auto md:flex-col md:overflow-visible">
							{nav.map(({ to, label, icon: Icon }) => (
								<li key={to} className="shrink-0">
									<NavLink
										to={to}
										className={cn(
											"flex items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-body transition-colors",
											pathname.startsWith(to)
												? "bg-surface3 font-medium text-heading"
												: "text-muted-foreground hover:bg-surface3 hover:text-heading",
										)}
									>
										<Icon className="size-4 shrink-0" />
										{label}
									</NavLink>
								</li>
							))}
						</ul>
					</nav>

					{/*
					 * Pinned to the bottom on desktop. Seat usage sits with the
					 * account rather than in the page, because it is a property of
					 * the org you are signed into, not of whichever screen you
					 * happen to be on.
					 */}
					<div className="hidden border-t border-divider px-5 py-3 md:block">
						{data?.seats ? (
							<div className="tabular mb-2 text-caption text-muted-foreground">
								{/* null purchased means UNLIMITED — interpolating it
								    renders "3 of  seats" on a valid contract. */}
								{data.seats.purchased == null
									? `${data.seats.active} seats in use`
									: `${data.seats.active} of ${data.seats.purchased} seats`}
							</div>
						) : null}
						{me?.email ? (
							<div
								className="mb-2 truncate text-caption text-muted-foreground"
								title={me.email}
							>
								{me.email}
							</div>
						) : null}
						<Button
							variant="ghost"
							size="sm"
							className="w-full justify-start px-2"
							onClick={() =>
								authService.signOut().finally(() => location.assign("/"))
							}
						>
							<LogOut className="mr-2 size-4" />
							Sign out
						</Button>
					</div>
				</aside>

				{/* On a phone the account row cannot live at the bottom of a
				    horizontal strip, so it rides along the top of the content. */}
				<div className="flex items-center gap-3 border-b border-divider bg-surface-primary px-5 py-2 md:hidden">
					<span className="min-w-0 flex-1 truncate text-caption text-muted-foreground">
						{me?.email}
					</span>
					<Button
						variant="ghost"
						size="sm"
						onClick={() =>
							authService.signOut().finally(() => location.assign("/"))
						}
					>
						<LogOut className="size-4" />
					</Button>
				</div>

				{/* The only thing that scrolls. min-h-0 is required: a flex child
				    defaults to min-height:auto and would refuse to shrink, pushing
				    the overflow onto the body and taking the sidebar with it. */}
				<main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
					<div className="mx-auto max-w-4xl px-6 py-8">{children}</div>
				</main>
			</div>
		</div>
	);
}
